import { useState, type ReactNode } from "react";
import { DialogHeader, DialogTitle, DialogFooter, Button } from "@repo/ui";

export type FieldConfig<T = unknown> = {
  name: string;
  label: string;
  defaultValue: T;
  render: (value: T, onChange: (value: T) => void, disabled: boolean) => ReactNode;
  validate?: (value: T) => string | null;
};

type Props<TData extends Record<string, unknown>> = {
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fields: FieldConfig<any>[];
  onSave: (data: TData) => Promise<void> | void;
  onClose?: () => void;
};

export const GenericFieldDialog = <TData extends Record<string, unknown>>({
  title,
  fields,
  onSave,
  onClose,
}: Props<TData>) => {
  const [formData, setFormData] = useState<Record<string, unknown>>(() => {
    const initialData: Record<string, unknown> = {};
    fields.forEach((field) => {
      initialData[field.name] = field.defaultValue;
    });
    return initialData;
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isPending, setIsPending] = useState(false);

  const hasChanges = fields.some((field) => {
    const currentValue = formData[field.name];
    const defaultValue = field.defaultValue;
    if (typeof currentValue === "object" && typeof defaultValue === "object") {
      return JSON.stringify(currentValue) !== JSON.stringify(defaultValue);
    }
    return currentValue !== defaultValue;
  });

  const handleFieldChange = (fieldName: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    if (errors[fieldName]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    fields.forEach((field) => {
      if (field.validate) {
        const error = field.validate(formData[field.name]);
        if (error) {
          newErrors[field.name] = error;
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsPending(true);
    try {
      await onSave(formData as TData);
      onClose?.();
    } catch {
      // Callers' mutations show the error toast; This just avoids Console errors for unhandled rejections (Ex: Uncaught (in promise) Error).
    } finally {
      setIsPending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isPending) {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <div className="flex flex-col gap-6" onKeyDown={handleKeyDown}>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        {fields.map((field) => (
          <div key={field.name} className="flex flex-col gap-2">
            <label htmlFor={field.name} className="text-gxe-gray text-sm font-medium">
              {field.label}
            </label>
            {field.render(
              formData[field.name],
              (value) => handleFieldChange(field.name, value),
              isPending
            )}
            {errors[field.name] && <p className="text-sm text-red-500">{errors[field.name]}</p>}
          </div>
        ))}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isPending || !hasChanges} loading={isPending}>
          Save
        </Button>
      </DialogFooter>
    </div>
  );
};
